/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-26 11:36:57
 **/

import { timer } from "rxjs";
import { nanoid } from "nanoid";
import { useBrowserState } from "@/components";
import MediaStreamUtility from "./MediaStreamUtility";

export type CanvasStreamCreateOption = {
	drawing?: (context: CanvasRenderingContext2D) => void;
	ended?: (ev: Event) => void;
};

// const LOGTAG = "#DummyStream#";

export default class DummyStream {
	#stream: MediaStream | null = null;
	#canvas: HTMLCanvasElement | null = null;

	constructor() {
		useBrowserState.subscribe((state) => {
			if (!state.visible) this.#draw();
		});
	}

	public get stream() {
		return this.#stream;
	}

	public set stream(value: MediaStream | null) {
		this.#stream = value;
	}

	public get canvas() {
		return this.#canvas;
	}

	public set canvas(value: HTMLCanvasElement | null) {
		this.#canvas = value;
	}

	public get has_canvas() {
		return this.#canvas !== null;
	}

	public get canvas_id() {
		return `dummy-video-${nanoid().toLowerCase()}`;
	}

	public async create(baseMedia?: MediaStream) {
		try {
			// console.debug(LOGTAG, `Dummy stream creating`);

			if (this.canvas) {
				await this.destroy();
			}
			if (this.canvas === null) {
				this.canvas = document.getElementById(this.canvas_id) as HTMLCanvasElement;
				if (!this.canvas) {
					this.canvas = document.createElement("canvas");
					this.canvas.id = this.canvas_id;
					this.canvas.width = 640;
					this.canvas.height = 360;

					this.canvas.style.position = "fixed";
					this.canvas.style.left = "0";
					this.canvas.style.top = "0";
					this.canvas.style.zIndex = "-99999";
					this.#get_parent.appendChild(this.canvas);
				}
			}

			this.#draw();

			this.#stream = new MediaStream(this.canvas.captureStream(5));
			if (baseMedia && baseMedia?.getAudioTracks().length > 0) {
				MediaStreamUtility.instance.track_clone(baseMedia.getAudioTracks()).forEach((track) => {
					if (this.#stream) this.#stream.addTrack(track);
				});
			}

			// console.debug(LOGTAG, `Dummy stream created`, this.#stream);
			return Promise.resolve(this.#stream);
		} catch (error: unknown) {
			return Promise.reject(error);
		}
	}

	public async destroy(timeout = 100) {
		return new Promise<void>((resolve) => {
			this.#stream?.getTracks().forEach((track) => track.stop());
			this.#stream = null;

			if (this.#canvas) {
				this.#get_parent.removeChild(this.#canvas);
				this.#canvas = null;
			}

			timer(timeout).subscribe(() => resolve());
		});
	}

	// private
	#draw() {
		if (this.#canvas !== null) {
			const context = this.#canvas.getContext("2d")!;
			context.beginPath();
			context.fillStyle = "black";
			context.fillRect(0, 0, 640, 360);
			context.closePath();
			if (useBrowserState.getState().visible) {
				// console.debug("==> draw requestAnimationFrame");
				requestAnimationFrame(this.#draw.bind(this));
			} else {
				// console.debug("==> draw setTimeout");
				window.setTimeout(this.#draw.bind(this), 100);
			}
		}
	}

	get #get_parent() {
		return document.getElementById("extra") ? (document.getElementById("extra") as HTMLElement) : document.body;
	}
}
